import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  IconButton,
  Box,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export default function NewItemsTableDialog({
  open,
  newItems = [],
  onOpenCreateDialog,
  createdIndices = [],
  onDone,
  onSkip,
  onCancel,
}) {
  const [itemsList, setItemsList] = useState([]);

  useEffect(() => {
    if (newItems && newItems.length > 0) {
      setItemsList(
        newItems.map((queueItem, index) => {
          const costRate = Number(queueItem.rate) || 0;
          const sellingPrice = Number(queueItem.price) || costRate;
          const margin = costRate > 0 && sellingPrice >= costRate ? ((sellingPrice - costRate) / costRate) * 100 : 0;

          const isCreated = createdIndices.includes(index);

          return {
            id: queueItem.plu || index,
            name: queueItem.item?.name || 'New Item',
            plu: queueItem.plu || '',
            rate: costRate,
            price: Math.round(sellingPrice * 100) / 100,
            profit_margin: Math.round(margin * 100) / 100,
            stock: Number(queueItem.stock) || 1,
            status: isCreated ? 'created' : 'pending',
            originalQueueItem: queueItem,
          };
        })
      );
    }
  }, [newItems, createdIndices]);

  useEffect(() => {
    if (createdIndices && createdIndices.length > 0) {
      setItemsList((prev) =>
        prev.map((item, idx) => (createdIndices.includes(idx) ? { ...item, status: 'created' } : item))
      );
    }
  }, [createdIndices]);

  const handleFieldChange = (index, field, value) => {
    setItemsList((prev) => {
      const updated = [...prev];
      const row = { ...updated[index], [field]: value };

      if (field === 'rate' || field === 'profit_margin') {
        const cost = Number(field === 'rate' ? value : row.rate) || 0;
        const margin = Number(field === 'profit_margin' ? value : row.profit_margin) || 0;
        row.price = Math.round(cost * (1 + margin / 100) * 100) / 100;
      } else if (field === 'price') {
        const priceVal = Number(value) || 0;
        const cost = Number(row.rate) || 0;
        row.profit_margin = cost > 0 && priceVal >= cost ? Math.round(((priceVal - cost) / cost) * 100 * 100) / 100 : 0;
      }

      updated[index] = row;
      return updated;
    });
  };

  const handleCreateSingleItem = (index) => {
    const targetRow = itemsList[index];
    if (!targetRow || targetRow.status !== 'pending') return;

    if (onOpenCreateDialog) {
      onOpenCreateDialog(targetRow, index);
    }
  };

  const handleSkipSingleItem = (index) => {
    setItemsList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], status: 'skipped' };
      return updated;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          if (onCancel) onCancel();
        }
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 16,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          padding: '16px 24px',
          width: '100%',
        }}
      >
        <Typography variant="h6" component="span" fontWeight="bold">
          New Items Detected ({itemsList.length})
        </Typography>
        <IconButton onClick={onCancel || onSkip} size="small" sx={{ color: '#fff', marginLeft: 'auto' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ paddingTop: '20px !important', paddingBottom: '16px' }}>
        <Typography variant="body2" color="textSecondary" sx={{ marginBottom: '16px' }}>
          The following items from the purchase bill do not exist in your inventory. Click <strong>Create</strong> on an
          item to set up and add it to your inventory & purchase list:
        </Typography>

        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px' }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>PLU</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Item Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">
                  Cost Rate
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">
                  Selling Price
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">
                  Margin (%)
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">
                  Stock Qty
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {itemsList.map((item, index) => (
                <TableRow key={index} hover sx={{ backgroundColor: item.status === 'created' ? '#f0fdf4' : 'inherit' }}>
                  <TableCell>{item.plu}</TableCell>
                  <TableCell>
                    {item.status === 'created' ? (
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        {item.name}
                      </Typography>
                    ) : (
                      <TextField
                        size="small"
                        value={item.name}
                        onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                        variant="outlined"
                        sx={{ '& .MuiOutlinedInput-input': { padding: '4px 8px', fontSize: '13px' } }}
                      />
                    )}
                  </TableCell>

                  <TableCell align="center">
                    {item.status === 'created' ? (
                      item.rate
                    ) : (
                      <TextField
                        size="small"
                        type="number"
                        value={item.rate}
                        onChange={(e) => handleFieldChange(index, 'rate', e.target.value)}
                        variant="outlined"
                        sx={{
                          width: '80px',
                          '& .MuiOutlinedInput-input': { padding: '4px 8px', fontSize: '13px', textAlign: 'center' },
                        }}
                      />
                    )}
                  </TableCell>

                  <TableCell align="center">
                    {item.status === 'created' ? (
                      item.price
                    ) : (
                      <TextField
                        size="small"
                        type="number"
                        value={item.price}
                        onChange={(e) => handleFieldChange(index, 'price', e.target.value)}
                        variant="outlined"
                        sx={{
                          width: '80px',
                          '& .MuiOutlinedInput-input': { padding: '4px 8px', fontSize: '13px', textAlign: 'center' },
                        }}
                      />
                    )}
                  </TableCell>

                  <TableCell align="center">
                    {item.status === 'created' ? (
                      `${item.profit_margin}%`
                    ) : (
                      <TextField
                        size="small"
                        type="number"
                        value={item.profit_margin}
                        onChange={(e) => handleFieldChange(index, 'profit_margin', e.target.value)}
                        variant="outlined"
                        sx={{
                          width: '70px',
                          '& .MuiOutlinedInput-input': { padding: '4px 8px', fontSize: '13px', textAlign: 'center' },
                        }}
                      />
                    )}
                  </TableCell>

                  <TableCell align="center">
                    {item.status === 'created' ? (
                      item.stock
                    ) : (
                      <TextField
                        size="small"
                        type="number"
                        value={item.stock}
                        onChange={(e) => handleFieldChange(index, 'stock', e.target.value)}
                        variant="outlined"
                        sx={{
                          width: '60px',
                          '& .MuiOutlinedInput-input': { padding: '4px 8px', fontSize: '13px', textAlign: 'center' },
                        }}
                      />
                    )}
                  </TableCell>

                  <TableCell align="center">
                    {item.status === 'created' ? (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Created"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    ) : item.status === 'skipped' ? (
                      <Chip label="Skipped" size="small" variant="outlined" />
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleCreateSingleItem(index)}
                          sx={{ textTransform: 'none', fontSize: '12px', padding: '2px 10px', borderRadius: '4px' }}
                        >
                          Create
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          color="secondary"
                          onClick={() => handleSkipSingleItem(index)}
                          sx={{ textTransform: 'none', fontSize: '12px', padding: '2px 8px', borderRadius: '4px' }}
                        >
                          Skip
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions
        sx={{ padding: '16px 24px', justifyContent: 'flex-end', gap: 1.5, borderTop: '1px solid #e2e8f0' }}
      >
        <Button
          onClick={onSkip || onCancel}
          variant="outlined"
          size="small"
          style={{
            borderColor: '#64748b',
            color: '#475569',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            padding: '6px 20px',
          }}
        >
          Skip
        </Button>

        <Button
          onClick={onDone || onSkip || onCancel}
          variant="contained"
          size="small"
          style={{
            backgroundColor: '#00a65a',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            padding: '6px 20px',
            color: '#fff',
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
